/**
 * RSA task catalog — the analysis types the single-project form offers and the
 * lookup tables that turn a task label into a Drive template id, a pipeline
 * service name and an `RSA_<code>` Source subfolder.
 *
 * Lifted out of RSAAnalysis.js (2026-09-07, run-queue dashboard) so the
 * multi-project "Run queue" panel and the shared payload builder
 * (rsaRunPayload.js) read the SAME tables the Run button uses — there must
 * never be a second copy of these ids.
 */

export const TASK_OPTIONS = [
  "Stopping Sight Distance (SSD)",
  "Passing Sight Distance (PSD)",
  "Flat Spot Analysis (FSA)",
  "Intersection Sight Distance (ISD)",
  "Sunglare Analysis (GLR)",
  "Guardrails Warrant (GRW)",
  "Horizontal Curve Analysis (HCA)",
  "Existing Guardrails (EXG)",
  "Embankment Analysis (EMB)",
  "Clear Zone Analysis (CLZ)",
  "Traffic Signs Analysis (TSA)",
  "Striping Plan (STP)",
];

export const DEFAULT_ANALYSIS_TEMPLATE_ID_MAPPING = {
  "Stopping Sight Distance (SSD)": "1rlzhq4UcaG89Z6-BgsvkzEoy07c-_FAh", // SSD
  "Passing Sight Distance (PSD)": "1WSPVWSMQm9ZNVZYi63glWnHtovalxiSw", // PSD
  "Flat Spot Analysis (FSA)": "1o5Tkz_1kZ3L9duI1m8iCUru3eg6kkj7o", // FSA
  "Intersection Sight Distance (ISD)": "14Wcpu_nadstIKGkMfydgdCn91JuTfN7a", // ISD
  "Sunglare Analysis (GLR)": "1OO7pFamfYbVi-kR7dHLy4zmi-dEUP1KD", // GLR (proper analysis template; old id 1SLUCIl… was a GIS-config stub)
  "Guardrails Warrant (GRW)": "1YrgG9gBrxU2qPP0OdeAF875RDvwyR5Ax", // Guardrail_Warrant
  "Horizontal Curve Analysis (HCA)": "1ui2FC_9umPQwJnVRxvGLFG3vheE2jzu8", // Horizontal
  "Existing Guardrails (EXG)": "1_UYE80G3n99vcEHPzOVFappGDXnEyThg", // Existing_Guardrails
  "Embankment Analysis (EMB)": "1wgbWg82DReXKI5SYbLVI4Ib6OGFbY7DY", // Embankment
  "Clear Zone Analysis (CLZ)": "1R7RxChS8YYc1v4zu_1moBj5pnVA-NKmQ", // ClearZone
  "Traffic Signs Analysis (TSA)": "1EeSuOGSLzVjUi6im-AArMX93NE5zy_Qs", // TSA.json
  "Striping Plan (STP)": "1V8qzRMvlRsloUtUG92GoIm5EhrmpSdzr", // STP.json
};

export const RSA_MODEL_ANALYSIS_TEMPLATE_ID_MAPPING = {
  "Intersection Sight Distance (ISD)": "1utdS8VLw2hoCf00xZbXhYtcTsWnWDdsm", // ISD-model.json
  "Passing Sight Distance (PSD)": "10bkKVtyerK5Qe20g7Rc15ncwDQn4daih", // PSD-model.json
  "Stopping Sight Distance (SSD)": "1rTfTDLA48ET--fAduVGsI0ad3LFssmLC", // SSD-model.json
  "Flat Spot Analysis (FSA)": "1UATrkaa_ASvlJejyfi0TekiM3nBfO9lC", // FSA-model.json
  "Sunglare Analysis (GLR)": "1irrUhf9npguUDAxHy29tILDiv7DTVO0t", // GLR-model.json
  "Horizontal Curve Analysis (HCA)": "1ElRBuXgNAHakGVVw11671QAn34IeQQKN", // Horizontal-model.json
};

export const RSA_MODEL_TASK_OPTIONS = [
  "Stopping Sight Distance (SSD)",
  "Passing Sight Distance (PSD)",
  "Flat Spot Analysis (FSA)",
  "Intersection Sight Distance (ISD)",
  "Sunglare Analysis (GLR)",
  "Horizontal Curve Analysis (HCA)",
];

export const TASK_FOLDER_CODE_TO_OPTION = {
  RSASSD: "Stopping Sight Distance (SSD)",
  RSAPSD: "Passing Sight Distance (PSD)",
  RSAFSA: "Flat Spot Analysis (FSA)",
  RSAISD: "Intersection Sight Distance (ISD)",
  RSAGRW: "Guardrails Warrant (GRW)",
  RSACLZ: "Clear Zone Analysis (CLZ)",
  RSAEMB: "Embankment Analysis (EMB)",
  RSAEXG: "Existing Guardrails (EXG)",
  RSAGLR: "Sunglare Analysis (GLR)",
  RSAHCA: "Horizontal Curve Analysis (HCA)",
  // Exact folder name only ("RSA_TSA" / "RSA TSA"); the whole name is
  // normalized to the code, so a road suffix would not match.
  RSATSA: "Traffic Signs Analysis (TSA)",
  RSASTP: "Striping Plan (STP)",
};

export const RSA_FOLDER_CODE_TO_SUFFIX = {
  RSASSD: "SSD",
  RSAPSD: "PSD",
  RSAFSA: "FSA",
  RSAISD: "ISD",
  RSAGLR: "GLR",
  RSAGRW: "GRW",
  RSAHCA: "HCA",
  RSAEXG: "EXG",
  RSAEMB: "EMB",
  RSACLZ: "CLZ",
  RSATSA: "TSA",
  RSASTP: "STP",
};

export const TASK_OPTION_TO_SERVICE = {
  "Stopping Sight Distance (SSD)": "stopping_sight_distance",
  "Passing Sight Distance (PSD)": "passing_sight_distance",
  "Flat Spot Analysis (FSA)": "flat_spot_analysis",
  "Intersection Sight Distance (ISD)": "intersection_sight_distance",
  "Sunglare Analysis (GLR)": "sunglare_analysis",
  "Guardrails Warrant (GRW)": "guardrails_warrant",
  "Horizontal Curve Analysis (HCA)": "horizontal",
  "Existing Guardrails (EXG)": "existing_guardrails",
  "Embankment Analysis (EMB)": "embankment",
  "Clear Zone Analysis (CLZ)": "clear_zone_analysis",
  "Traffic Signs Analysis (TSA)": "traffic_signs",
  "Striping Plan (STP)": "striping_plan",
};

// Services the run form never bills. The Striping Plan itself is free; PSD it needs
// for roads without usable earlier results is added and billed as PSD by the backend
// at submit (Backend/Pipeline/utils/striping_plan.py apply_billing), so the form must
// not count STP files or miles anywhere (spec 2026-10-14 Part F).
export const UNBILLED_SERVICES = new Set(["striping_plan"]);

export const TASK_OPTION_TO_FOLDER_CODE = Object.entries(
  TASK_FOLDER_CODE_TO_OPTION,
).reduce((acc, [folderCode, taskOption]) => {
  acc[taskOption] = folderCode;
  return acc;
}, {});

/** "SSD" → "Stopping Sight Distance (SSD)" (config_type as the backend names it). */
export const taskOptionForConfigType = (configType) => {
  const suffix = String(configType ?? "")
    .trim()
    .toUpperCase();
  if (!suffix) return null;
  const folderCode = Object.keys(RSA_FOLDER_CODE_TO_SUFFIX).find(
    (code) => RSA_FOLDER_CODE_TO_SUFFIX[code] === suffix,
  );
  return folderCode ? TASK_FOLDER_CODE_TO_OPTION[folderCode] || null : null;
};

/** Template-id table for a project type ("rsa-model" vs everything else). */
export const templateMappingForProjectType = (projectType) =>
  String(projectType || "")
    .trim()
    .toLowerCase() === "rsa-model"
    ? RSA_MODEL_ANALYSIS_TEMPLATE_ID_MAPPING
    : DEFAULT_ANALYSIS_TEMPLATE_ID_MAPPING;

export const taskOptionsForProjectType = (projectType) =>
  String(projectType || "")
    .trim()
    .toLowerCase() === "rsa-model"
    ? RSA_MODEL_TASK_OPTIONS
    : TASK_OPTIONS;
